import moment from 'moment'
import { ISO_DATE_FORMAT, ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import { CHARTER } from 'constants/provider'
import { FlightsClassTypes } from 'constants/flight'
import {
  getDistanceEqFilter,
  getDistanceLteFilter,
} from 'lib/search/searchUtils'
import { SOURCE_TYPES } from 'constants/source'
import { HOTEL_OFFERS_LIMIT, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'

const holidayTypePassengerCounts = new Map<string, number>([
  ['solo traveler', 1],
  ['solo traveller', 1],
  ['romantic', 2],
  ['honeymoon', 2],
])

function normaliseHolidayType(holidayType: string): string {
  let decodedHolidayType = holidayType
  try {
    decodedHolidayType = decodeURIComponent(holidayType)
  } catch {
    decodedHolidayType = holidayType
  }

  return decodedHolidayType
    .replace(/\+/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

export function getMaldivesDirectPassengerCountForHolidayTypes(holidayTypes?: Array<string>): number | undefined {
  return holidayTypes
    ?.map(holidayType => holidayTypePassengerCounts.get(normaliseHolidayType(holidayType)))
    .find((passengerCount): passengerCount is number => passengerCount !== undefined)
}

export function buildMaldivesDirectRoomsForPassengerCount(passengerCount: number): Array<App.Occupants> {
  return [{
    adults: passengerCount,
    children: 0,
    infants: 0,
    teenagers: 0,
    childrenAge: [],
  }]
}

export function getMaldivesBundleCheckOutDate(checkInDate: string | undefined, nights: number): string | null {
  if (!checkInDate) {
    return null
  }

  return moment(checkInDate).add(nights, 'days').format(ISO_DATE_FORMAT)
}

/**
 * Weekday enum
 */
export enum Weekday {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

/**
 * Get the next occurrence of a specific weekday in the current month
 * @param weekday - The target weekday
 * @returns The next occurrence of the weekday, or null if not found in current month
 */
export function getNextWeekdayInCurrentMonth(weekday: Weekday): moment.Moment | null {
  const today = moment()
  const currentMonth = today.month()
  let date = today.clone().add(1, 'day')

  // Find the next occurrence of the specified weekday within the current month
  while (date.month() === currentMonth) {
    if (date.day() === weekday) {
      return date
    }
    date = date.add(1, 'day')
  }

  return null
}

/**
 * Get the next occurrence of a specific weekday from today
 * @param weekday - The target weekday
 * @param startDate - Optional start date, defaults to today
 * @returns The next occurrence of the weekday
 */
export function getNextWeekday(weekday: Weekday, startDate?: moment.Moment): moment.Moment {
  const start = startDate ? startDate.clone() : moment()
  const nextDate = start.clone()

  // If today is the target weekday and we're looking from a future date, include it
  if (nextDate.day() === weekday && startDate) {
    return nextDate
  }

  // Find next occurrence
  while (nextDate.day() !== weekday) {
    nextDate.add(1, 'day')
  }

  return nextDate
}

/**
 * Get all occurrences of a specific weekday in a given month
 * @param weekday - The target weekday
 * @param monthOffset - Month offset from current month (0 = current, 1 = next month, etc.)
 * @param includeToday - Whether to include dates from today onwards (for current month only)
 * @returns Array of dates for the specified weekday in that month
 */
export function getAllWeekdaysInMonth(
  weekday: Weekday,
  monthOffset: number = 0,
  includeToday: boolean = true,
): Array<moment.Moment> {
  const targetMonth = moment().add(monthOffset, 'months')
  const monthValue = targetMonth.month()
  const dates: Array<moment.Moment> = []

  if (monthOffset === 0 && includeToday) {
    // Current month: start from today or next occurrence
    const current = moment()

    // Find first occurrence from today onwards
    while (current.day() !== weekday && current.month() === monthValue) {
      current.add(1, 'day')
    }

    // Add all occurrences in current month
    while (current.month() === monthValue) {
      dates.push(current.clone())
      current.add(7, 'days')
    }
  } else {
    // Future months: get all occurrences in that month
    const firstDayOfMonth = targetMonth.clone().startOf('month')
    const current = firstDayOfMonth.clone()

    // Find first occurrence
    while (current.day() !== weekday) {
      current.add(1, 'day')
    }

    // Add all occurrences
    while (current.month() === monthValue) {
      dates.push(current.clone())
      current.add(7, 'days')
    }
  }

  return dates
}

/**
 * Get all occurrences of a specific weekday for multiple future months
 * @param weekday - The target weekday
 * @param numberOfMonths - Number of months to look ahead (default 6)
 * @returns Object with month names as keys and arrays of dates as values
 *
 * @example
 * const saturdays = getWeekdaysByMonth(Weekday.Saturday, 6)
 * // Returns: { '2026-10': [dates], '2026-11': [dates], ... }
 */
export function getWeekdaysByMonth(
  weekday: Weekday,
  numberOfMonths: number = 6,
): Record<string, Array<string>> {
  const monthsData: Record<string, Array<string>> = {}

  // Determine start month: April 2026 if today is before it, otherwise current month
  // ToDo: cleanup this logic when time pass April 2026
  const targetStartDate = moment('2026-04-01').startOf('month')
  const today = moment()
  const todayStartOfMonth = today.clone().startOf('month')
  const startMonth = today.isBefore(targetStartDate) ? targetStartDate : todayStartOfMonth

  // Calculate offset from today to start month
  const startMonthOffset = Math.round(startMonth.diff(todayStartOfMonth, 'months', true))

  for (let i = 0; i < numberOfMonths; i++) {
    const monthOffset = startMonthOffset + i
    const targetMonth = todayStartOfMonth.clone().add(monthOffset, 'months')
    const monthKey = targetMonth.format(ISO_DATE_SHORT_FORMAT)
    const dates = getAllWeekdaysInMonth(weekday, monthOffset, monthOffset === 0)

    monthsData[monthKey] = dates.map(date => date.format(ISO_DATE_FORMAT))
  }

  return monthsData
}

/**
 * Get an array of dates for a specific weekday with offset days
 * @param weekday - The target weekday
 * @param offsets - Array of offset days, e.g. [0, 5, 11] returns base date, +5 days, +11 days
 * @returns Array of moment dates, or empty array if base date not found
 */
export function getWeekdayWithOffsets(
  weekday: Weekday,
  offsets: Array<number> = [0, 5, 11],
): Array<moment.Moment> {
  const baseDate = getNextWeekdayInCurrentMonth(weekday)

  if (!baseDate) {
    return []
  }

  return offsets.map(offset => baseDate.clone().add(offset, 'days'))
}

/**
 * Format date to YYYY-MM-DD format
 * @param date - The moment date to format
 * @returns Formatted date string
 */
export function formatDate(date: moment.Moment): string {
  return date.format(ISO_DATE_FORMAT)
}

/**
 * Main method: Get the next occurrence of a weekday in current month and dates with specified offsets
 * @param weekday - The target weekday
 * @param customOffsets - Custom offset days array, defaults to [5, 11]
 * @returns Object containing base date and offset dates, or null if not found
 *
 * @example
 * // Get next Monday and dates 5 days and 11 days later
 * const result = getWeekdayDates(Weekday.Monday, [5, 11])
 * // Returns: { baseDate: Moment, offsetDates: [Moment, Moment], formatted: { base: 'YYYY-MM-DD', offsets: ['YYYY-MM-DD', 'YYYY-MM-DD'] } }
 */
export function getWeekdayDates(
  weekday: Weekday,
  customOffsets: Array<number> = [5, 11],
) {
  const baseDate = getNextWeekdayInCurrentMonth(weekday)

  if (!baseDate) {
    return null
  }

  const offsetDates = customOffsets.map(offset => baseDate.clone().add(offset, 'days'))

  return {
    baseDate,
    offsetDates,
    formatted: {
      base: formatDate(baseDate),
      offsets: offsetDates.map(formatDate),
    },
  }
}

/**
 * Common interface for flight search parameters
 */
export interface FlightSearchParams {
  departureAirport: string
  arrivalAirport: string
  departureDate: string | moment.Moment
  returnDate: string | moment.Moment
  occupancies: Array<App.Occupants>
  fareClass?: FlightsClassTypes
  fareType?: string
  currency?: string
  source?: string
  feederOriginAirportCode?: string
}

/**
 * Common interface for hotel search parameters
 */
export interface HotelSearchParams {
  destinationId: string
  destinationName: string
  checkIn: string | moment.Moment
  checkOut: string | moment.Moment
  rooms: Array<App.Occupants>
  limit?: number
  fareClass?: App.OfferListFilters['fareClass']
  feederOriginAirportCode?: string
  charterNumberOfNights?: number
}

/**
 * Build flight search payload for Maldives charter
 * @param params - Flight search parameters
 * @returns Formatted payload for fetchFlights action
 *
 * @example
 * const payload = buildFlightSearchPayload({
 *   departureAirport: 'MEL',
 *   arrivalAirport: 'MLE',
 *   departureDate: '2025-10-25',
 *   returnDate: '2025-11-01',
 *   occupancies: [{ adults: 2, children: 0 }],
 *   fareClass: 'economy',
 *   currency: 'AUD'
 * })
 */
export function buildFlightSearchPayload(params: FlightSearchParams) {
  const {
    departureAirport,
    arrivalAirport,
    departureDate,
    returnDate,
    occupancies,
    fareClass = FlightsClassTypes.ECONOMY,
    fareType = 'return',
    currency = 'AUD', // Default to AUD if not provided
    source = CHARTER,
    feederOriginAirportCode,
  } = params

  const formatDateString = (date: string | moment.Moment): string => {
    return moment.isMoment(date) ? date.format(ISO_DATE_FORMAT) : date
  }

  return {
    itinerary: [
      {
        departureAirport,
        arrivalAirport,
        departureDate: formatDateString(departureDate),
      },
      {
        departureAirport: arrivalAirport,
        arrivalAirport: departureAirport,
        departureDate: formatDateString(returnDate),
      },
    ],
    occupancies,
    fareClass,
    fareType,
    currency,
    source,
    feederOriginAirportCode,
  }
}

/**
 * Build hotel offer filters for Maldives Direct charter bundle
 * This function builds the complete filters including rawFilters for key generation and final bundleFilters
 * @param params - Hotel search parameters
 * @returns Final formatted filters for useOfferList hook
 *
 */
export function buildBundleOfferFilters(params: HotelSearchParams): App.OfferListFilters {
  const {
    destinationId,
    checkIn,
    checkOut,
    rooms,
    limit = HOTEL_OFFERS_LIMIT,
    fareClass,
    feederOriginAirportCode,
    charterNumberOfNights,
  } = params

  const formatDateString = (date: string | moment.Moment): string => {
    return moment.isMoment(date) ? date.format(ISO_DATE_FORMAT) : date
  }

  const bundleFilters: App.OfferListFilters = {
    destinationId,
    checkIn: formatDateString(checkIn),
    checkOut: formatDateString(checkOut),
    offerTypes: [OFFER_TYPE_HOTEL],
    isStream: true,
    limit,
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    distanceEq: getDistanceEqFilter('destination'),
    distanceLte: getDistanceLteFilter('destination'),
    source: SOURCE_TYPES.SEARCH,
    rooms: rooms.map(room => ({
      ...room,
      teenagers: room.teenagers ?? 0,
    })),
    fareClass,
    feederOriginAirportCode,
    charterNumberOfNights,
    charterBundle: true,
  }

  return bundleFilters
}
